/**
 * PlaneCredentialCard — the signed-in person's own Plane account.
 *
 * Lives inside Settings → Profile rather than Settings → Plane on purpose: this
 * is the one Plane setting that is nobody else's business. Everything an agent
 * does on the board — a comment, a state change, an assignment, a page — is
 * recorded by Plane as done by *this* account, so the only person who may type
 * the password is its owner. There is no admin route that writes it for them.
 *
 * The password is write-only, like every other credential field in this app: the
 * server answers with a status and, when Plane refused, with what it said. There
 * is nothing to rehydrate into the field on reload, which is what makes the form
 * safe to re-open.
 *
 * Saving verifies immediately, because a credential that was never proven is
 * indistinguishable from a wrong one — and the API token Taskin mints from it
 * can only be issued while the password is known to work. The workspaces that
 * come back are shown for a practical reason: they are exactly the boards this
 * account can reach, so a project linked to a workspace missing from that list
 * will not work for this person and they can see it here rather than discover it
 * through an agent failing mid-task.
 */
import { useEffect, useState } from "react";

import type { UserPlaneCredentialView } from "@taskin-code/contracts";

import { PlaneClient } from "../../lib/planeClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const STATUS_LABEL: Record<UserPlaneCredentialView["status"], string> = {
  none: "Sin probar",
  valid: "Válida",
  invalid: "Rechazada",
};

const STATUS_CLASS: Record<UserPlaneCredentialView["status"], string> = {
  none: "border-border bg-muted/40 text-muted-foreground",
  valid: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  invalid: "border-red-500/40 bg-red-500/10 text-red-300",
};

export function PlaneCredentialCard() {
  const [credential, setCredential] = useState<UserPlaneCredentialView | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void PlaneClient.readMyCredential()
      .then((value) => {
        if (cancelled) return;
        setCredential(value);
        setEmail(value.planeEmail ?? "");
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(
          cause instanceof Error ? cause.message : "No se pudo leer tu credencial de Plane.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const result = await PlaneClient.writeMyCredential({
        planeEmail: email.trim(),
        password,
      });
      setCredential(result);
      setPassword("");
      setMessage(
        result.status === "valid"
          ? "Plane aceptó tu cuenta. Lo que hagan los agentes en tus hilos saldrá a tu nombre."
          : null,
      );
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "No se pudo guardar la credencial.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const result = await PlaneClient.deleteMyCredential();
      setCredential(result);
      setEmail("");
      setPassword("");
      setMessage("Credencial borrada y token revocado en Plane.");
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "No se pudo borrar la credencial.");
    } finally {
      setBusy(false);
    }
  };

  const status = credential?.status ?? "none";
  const configured = credential?.planeEmail !== null && credential?.planeEmail !== undefined;
  const canSave = email.trim().length > 0 && password.length > 0;

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border/70 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">Tu cuenta de Plane</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Los agentes que trabajan en tus hilos comentan, cambian estados y escriben páginas con
            esta cuenta. Sin ella no pueden tocar Plane: no hay una cuenta compartida a la que
            recurrir.
          </p>
        </div>
        {configured ? (
          <span
            className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] ${STATUS_CLASS[status]}`}
          >
            {STATUS_LABEL[status]}
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Label className="text-xs" htmlFor="plane-credential-email">
            Correo de Plane
          </Label>
          <Input
            id="plane-credential-email"
            value={email}
            disabled={busy}
            autoComplete="off"
            placeholder="tu.correo@empresa.com"
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Label className="text-xs" htmlFor="plane-credential-password">
            Contraseña
          </Label>
          <Input
            id="plane-credential-password"
            type="password"
            value={password}
            disabled={busy}
            autoComplete="new-password"
            placeholder={configured ? "•••••••• (guardada)" : "Contraseña de Plane"}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </div>
      </div>

      {credential !== null && credential.workspaceSlugs.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          Workspaces a los que llega tu cuenta:{" "}
          <span className="font-mono">{credential.workspaceSlugs.join(", ")}</span>
        </p>
      ) : null}

      {credential?.lastError ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {credential.lastError}
        </div>
      ) : null}

      {error ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          disabled={busy || !canSave}
          onClick={() => {
            void save();
          }}
        >
          {busy ? "Comprobando…" : "Guardar y comprobar"}
        </Button>
        {configured ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => {
              void remove();
            }}
          >
            Borrar
          </Button>
        ) : null}
        {message ? <span className="text-xs text-muted-foreground">{message}</span> : null}
      </div>

      <p className="text-xs text-muted-foreground">
        Se guarda cifrada y nunca se muestra de vuelta. Taskin la usa para emitir un token de API a
        tu nombre; al borrarla aquí, ese token se revoca en Plane. Una cuenta con verificación en
        dos pasos activada no puede usarse.
      </p>
    </div>
  );
}
